import { useState, useEffect, useCallback } from 'react'
import {
  generateRounds,
  getWalletDenoms,
  type Round,
  type McqOption,
  type MoneyDenomination,
} from './MoneyData'

export type { MoneyDenomination, Round, McqOption }
export type Difficulty = 'easy' | 'medium' | 'hard'
export type MoneyMode  = 'identify' | 'count' | 'pay'

export interface DishItem {
  denom:      MoneyDenomination
  instanceId: string
}

export interface MoneyGameState {
  rounds:          Round[]
  currentRoundIdx: number
  totalRounds:     number
  // MCQ feedback (identify + count modes)
  correctFlashIdx: number | null   // option index with green flash
  wrongFlashIdx:   number | null   // option index with shake/red flash
  // Pay mode
  dish:            DishItem[]
  payCorrect:      boolean         // green flash when dish total matches
  overBudget:      boolean         // brief red indicator when dish total exceeds price
  // Progress
  moves:           number
  correctCount:    number
  startTime:       number | null
  endTime:         number | null
  gameComplete:    boolean
  // Wallet (eligible denominations for pay mode)
  wallet:          MoneyDenomination[]
}

let instanceCounter = 0

// ── Pure init ─────────────────────────────────────────────────

function initState(
  difficulty: Difficulty,
  mode:       MoneyMode,
  denoms:     MoneyDenomination[],
  language:   string,
): MoneyGameState {
  const rounds      = generateRounds(denoms, mode, difficulty, language)
  const wallet      = mode === 'pay' ? getWalletDenoms(denoms, difficulty) : []
  const totalRounds = rounds.length

  return {
    rounds,
    currentRoundIdx: 0,
    totalRounds,
    correctFlashIdx: null,
    wrongFlashIdx:   null,
    dish:            [],
    payCorrect:      false,
    overBudget:      false,
    moves:           0,
    correctCount:    0,
    startTime:       null,
    endTime:         null,
    gameComplete:    totalRounds === 0,
    wallet,
  }
}

// ── Hook ──────────────────────────────────────────────────────

export function useMoneyGame(
  difficulty: Difficulty,
  mode:       MoneyMode,
  denoms:     MoneyDenomination[],
  language:   string,
) {
  const [state, setState] = useState<MoneyGameState>(() =>
    initState(difficulty, mode, denoms, language),
  )

  const resetGame = useCallback(() => {
    setState(initState(difficulty, mode, denoms, language))
  }, [difficulty, mode, denoms, language])

  // Re-init when denominations first load (empty → populated) or settings change
  useEffect(() => {
    setState(initState(difficulty, mode, denoms, language))
  }, [difficulty, mode, denoms, language])

  // Advance to next round after correct-answer flash (identify + count)
  useEffect(() => {
    if (state.correctFlashIdx === null) return
    const timer = setTimeout(() => {
      setState((prev) => {
        const nextIdx      = prev.currentRoundIdx + 1
        const gameComplete = nextIdx >= prev.totalRounds
        return {
          ...prev,
          currentRoundIdx: nextIdx,
          correctFlashIdx: null,
          wrongFlashIdx:   null,
          gameComplete,
          endTime: gameComplete ? Date.now() : prev.endTime,
        }
      })
    }, 700)
    return () => clearTimeout(timer)
  }, [state.correctFlashIdx])

  // Advance to next round after pay-mode correct flash
  useEffect(() => {
    if (!state.payCorrect) return
    const timer = setTimeout(() => {
      setState((prev) => {
        const nextIdx      = prev.currentRoundIdx + 1
        const gameComplete = nextIdx >= prev.totalRounds
        return {
          ...prev,
          currentRoundIdx: nextIdx,
          dish:            [],
          payCorrect:      false,
          gameComplete,
          endTime: gameComplete ? Date.now() : prev.endTime,
        }
      })
    }, 700)
    return () => clearTimeout(timer)
  }, [state.payCorrect])

  // Clear wrong-answer flash after animation
  useEffect(() => {
    if (state.wrongFlashIdx === null) return
    const timer = setTimeout(() => {
      setState((prev) => ({ ...prev, wrongFlashIdx: null }))
    }, 650)
    return () => clearTimeout(timer)
  }, [state.wrongFlashIdx])

  // Clear over-budget indicator
  useEffect(() => {
    if (!state.overBudget) return
    const timer = setTimeout(() => {
      setState((prev) => ({ ...prev, overBudget: false }))
    }, 800)
    return () => clearTimeout(timer)
  }, [state.overBudget])

  // ── MCQ actions (identify + count) ───────────────────────────

  const selectOption = useCallback((optionIdx: number, option: McqOption) => {
    setState((prev) => {
      if (prev.correctFlashIdx !== null || prev.gameComplete) return prev

      const startTime = prev.startTime ?? Date.now()
      const newMoves  = prev.moves + 1

      if (!option.isCorrect) {
        return { ...prev, wrongFlashIdx: optionIdx, moves: newMoves, startTime }
      }

      return {
        ...prev,
        correctFlashIdx: optionIdx,
        moves:           newMoves,
        correctCount:    prev.correctCount + 1,
        startTime,
      }
    })
  }, [])

  // ── Pay mode actions ──────────────────────────────────────────

  const addToDish = useCallback((denom: MoneyDenomination) => {
    setState((prev) => {
      if (prev.payCorrect || prev.gameComplete) return prev

      const currentRound = prev.rounds[prev.currentRoundIdx]
      if (currentRound?.type !== 'pay') return prev

      const startTime  = prev.startTime ?? Date.now()
      const instanceId = `dish-${instanceCounter++}`
      const newDish    = [...prev.dish, { denom, instanceId }]
      const dishTotal  = newDish.reduce((s, i) => s + i.denom.valueCents, 0)
      const newMoves   = prev.moves + 1

      if (dishTotal === currentRound.targetCents) {
        return {
          ...prev,
          dish:         newDish,
          payCorrect:   true,
          moves:        newMoves,
          correctCount: prev.correctCount + 1,
          startTime,
        }
      }

      if (dishTotal > currentRound.targetCents) {
        return { ...prev, dish: newDish, overBudget: true, moves: newMoves, startTime }
      }

      return { ...prev, dish: newDish, moves: newMoves, startTime }
    })
  }, [])

  const removeFromDish = useCallback((instanceId: string) => {
    setState((prev) => {
      if (prev.payCorrect || prev.gameComplete) return prev

      const currentRound = prev.rounds[prev.currentRoundIdx]
      if (currentRound?.type !== 'pay') return prev

      const newDish   = prev.dish.filter(i => i.instanceId !== instanceId)
      const dishTotal = newDish.reduce((s, i) => s + i.denom.valueCents, 0)

      if (dishTotal === currentRound.targetCents) {
        return {
          ...prev,
          dish:         newDish,
          payCorrect:   true,
          correctCount: prev.correctCount + 1,
          overBudget:   false,
        }
      }

      return { ...prev, dish: newDish, overBudget: false }
    })
  }, [])

  const clearDish = useCallback(() => {
    setState((prev) => {
      if (prev.payCorrect) return prev
      return { ...prev, dish: [], overBudget: false }
    })
  }, [])

  return { state, resetGame, selectOption, addToDish, removeFromDish, clearDish }
}
